/**
 * ExtranetSpacePage — lista de páginas dentro de um espaço.
 */
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useExtranetPages } from '@/hooks/kb/useKbExtranet';
import { Card } from '@/components/ui/card';
import { Loader2, ChevronLeft, FileText } from 'lucide-react';

export default function ExtranetSpacePage() {
  const { tenantSlug = '', spaceSlug = '' } = useParams<{
    tenantSlug: string;
    spaceSlug: string;
  }>();
  const { data: pages, isLoading } = useExtranetPages(tenantSlug, spaceSlug);

  return (
    <div className="space-y-4">
      <Helmet>
        <title>{spaceSlug}</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <Link
        to={`/extranet/${tenantSlug}`}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" />
        Voltar aos espaços
      </Link>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : !pages || pages.length === 0 ? (
        <Card className="p-10 text-center text-sm text-muted-foreground">
          Nenhuma página disponível neste espaço.
        </Card>
      ) : (
        <div className="space-y-2">
          {pages.map((p: any) => (
            <Link
              key={p.page_id}
              to={`/extranet/${tenantSlug}/espacos/${spaceSlug}/${p.slug}`}
              className="block"
            >
              <Card className="p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{p.title}</div>
                    {p.summary && (
                      <div className="text-sm text-muted-foreground line-clamp-2 mt-0.5">
                        {p.summary}
                      </div>
                    )}
                    {p.updated_at && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Atualizado em{' '}
                        {new Date(p.updated_at).toLocaleDateString('pt-BR')}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
